import { useMemo } from 'react';

import axios from 'axios';

import { getStoredAuth } from '@/lib/authStorage';
import { baseUrl } from '@/lib/config';
import { configureUploadRetry } from '@/lib/retry';
import { attachAccessToken, attachSessionExpiry, attachSessionRecovery } from '@/lib/sessionRecovery';

import { useAuthData } from './useApiKey';
import { useLogout } from './useLogout';

export interface UseAuthAxiosOptions {
    /** Upload-only retries stay opt-in because this client is also used for background requests. */
    retry?: boolean;
    /** Upload-only recovery stays opt-in so background requests cannot renew an idle session. */
    recoverSession?: boolean;
}

export function useAuthAxios(options: UseAuthAxiosOptions = {}) {
    const { accessToken, setAuthData } = useAuthData();
    const logout = useLogout({ isSessionExpired: true });
    const { retry, recoverSession } = options;

    const instance = useMemo(() => {
        const axiosInstance = axios.create({
            baseURL: baseUrl,
        });

        attachAccessToken(axiosInstance, () => getStoredAuth()?.accessToken ?? '', () => getStoredAuth()?.refreshToken ?? '');
        const deps = {
            getAccessToken: () => getStoredAuth()?.accessToken ?? '',
            getRefreshToken: () => getStoredAuth()?.refreshToken ?? '',
            onRefreshed: setAuthData,
            onSessionEnded: logout,
        };
        if (recoverSession) attachSessionRecovery(axiosInstance, deps);
        else attachSessionExpiry(axiosInstance, deps);

        if (retry) configureUploadRetry(axiosInstance);

        return axiosInstance;
    }, [logout, retry, recoverSession, setAuthData]);

    return { client: instance, enabled: !!accessToken };
}
